import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Button,
  Collapse,
  Grid,
  Chip,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  TextField,
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  CheckCircle,
  Business,
  LocationOn,
  Phone,
  Email,
  Language,
  Schedule,
  Payment,
  Visibility,
  AutoAwesome,
  Delete,
  Person,
  PhoneAndroid,
  Web,
  CloudDownload,
  Edit,
  Save,
  Cancel,
} from '@mui/icons-material';
import firebase from 'firebase/app';
import TabDescription from './TabDescription';
import 'firebase/firestore';
import axios from 'axios';
import { useRegionContext } from '../../region/RegionContext';
import { queryAllRegions } from '../../utils/queryAllRegions';
import MediaPreviewDialog from './MediaPreviewDialog';
import AdminMenuCVImport from './AdminMenuCVImport';

const SOURCE_LABELS = {
  mobile_user_submit: { label: 'Mobile Submit', color: 'info', icon: <PhoneAndroid sx={{ fontSize: 14 }} /> },
  website_claim: { label: 'Website Claim', color: 'secondary', icon: <Web sx={{ fontSize: 14 }} /> },
  website_owner: { label: 'Website Owner', color: 'secondary', icon: <Web sx={{ fontSize: 14 }} /> },
  google_import: { label: 'Google Import', color: 'default', icon: <CloudDownload sx={{ fontSize: 14 }} /> },
};

const getSourceInfo = (business) => {
  if (business.bizSource) return SOURCE_LABELS[business.bizSource] || { label: business.bizSource, color: 'default' };
  if (business.sourceGoogle || business.importedAt) return SOURCE_LABELS.google_import;
  if (business.claimStatus === 'pending' || business.claimStatus === 'claimed') return SOURCE_LABELS.website_claim;
  return { label: 'Unknown', color: 'default' };
};

const PAGE_SIZE = 25;

const NewBusinesses = () => {
  const { db, endpoints, isGlobal, allRegionDbs } = useRegionContext();
  const [businesses, setBusinesses] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [verifying, setVerifying] = useState(null);
  const [sendingStripe, setSendingStripe] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [error, setError] = useState(null);
  const [previewBusiness, setPreviewBusiness] = useState(null);
  const [cvVenue, setCvVenue] = useState(null);
  const [ownerMap, setOwnerMap] = useState({});
  const [staffMap, setStaffMap] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [editFields, setEditFields] = useState({});

  useEffect(() => {
    loadNewBusinesses();
  }, [db, isGlobal, allRegionDbs]);

  const loadNewBusinesses = async () => {
    try {
      setLoading(true);

      let data;
      if (isGlobal && allRegionDbs) {
        data = await queryAllRegions(allRegionDbs, null, (regionDb) =>
          regionDb.collection('Business').where('bizVerified', '==', false).limit(500)
        );
      } else {
        const snapshot = await db
          .collection('Business')
          .where('bizVerified', '==', false)
          .limit(500)
          .get();

        data = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
      }

      // Sort client-side — timestamp may be missing on imported venues
      data.sort((a, b) => {
        const aTime = (a.timestamp || a.importedAt)?.toDate?.()?.getTime?.() || 0;
        const bTime = (b.timestamp || b.importedAt)?.toDate?.()?.getTime?.() || 0;
        return bTime - aTime;
      });

      setBusinesses(data);
      setPage(0);

      // Load owner info for all businesses (staff loaded on expand-click)
      await loadOwnerInfo(data, db);
    } catch (err) {
      console.error('Error loading businesses:', err);
      setError('Failed to load new businesses');
    } finally {
      setLoading(false);
    }
  };

  const loadOwnerInfo = async (data, db) => {
    const uids = [...new Set(data.map(b => b.uid || b.owner).filter(Boolean))];
    if (uids.length === 0) return;

    // Firestore 'in' queries accept max 30 values
    const map = {};
    for (let i = 0; i < uids.length; i += 30) {
      const batch = uids.slice(i, i + 30);
      const snap = await db.collection('Users').where(firebase.firestore.FieldPath.documentId(), 'in', batch).get();
      snap.docs.forEach(doc => {
        const d = doc.data();
        map[doc.id] = { name: d.userName || d.userEmail || doc.id, email: d.userEmail || '', signupSource: d.signupSource || '' };
      });
    }
    setOwnerMap(map);
  };

  const handleToggleExpand = async (id) => {
    if (expandedId === id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(id);

    if (staffMap[id] === undefined) {
      try {
        const staffSnap = await db.collection('Business').doc(id).collection('Staff').get();
        if (!staffSnap.empty) {
          setStaffMap(prev => ({ ...prev, [id]: staffSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })) }));
        } else {
          setStaffMap(prev => ({ ...prev, [id]: [] }));
        }
      } catch {
        setStaffMap(prev => ({ ...prev, [id]: [] }));
      }
    }
  };

  const handleVerifyBusiness = async (business) => {
    if (!window.confirm(`Verify ${business.bizName}?`)) {
      return;
    }

    try {
      setVerifying(business.id);

      await db.collection('Business').doc(business.id).update({
        bizVerified: true,
        verifiedAt: firebase.firestore.FieldValue.serverTimestamp(),
      });

      alert(`${business.bizName} has been verified.`);

      loadNewBusinesses();
    } catch (err) {
      console.error('Error verifying business:', err);
      alert('Failed to verify business. Please try again.');
    } finally {
      setVerifying(null);
    }
  };

  const handleDeleteBusiness = async (business) => {
    if (!window.confirm(`Delete "${business.bizName}"? This cannot be undone.`)) return;
    try {
      setDeleting(business.id);
      await db.collection('Business').doc(business.id).delete();
      setBusinesses(prev => prev.filter(b => b.id !== business.id));
    } catch (err) {
      console.error('Error deleting business:', err);
      alert('Failed to delete business.');
    } finally {
      setDeleting(null);
    }
  };

  const handleSendStripeOnboarding = async (business) => {
    if (!window.confirm(`Send Stripe onboarding link to ${business.bizName}?`)) {
      return;
    }

    try {
      setSendingStripe(business.id);

      await axios.post(
        endpoints.sendStripeOnboarding,
        {
          bizId: business.id,
          bizName: business.bizName,
          bizEmail: business.bizEmail || business.userEmail,
        }
      );

      alert(`Stripe onboarding link sent to ${business.bizName}.`);
    } catch (err) {
      console.error('Failed to send Stripe onboarding:', err);
      alert('Failed to send Stripe onboarding link. Please try again.');
    } finally {
      setSendingStripe(null);
    }
  };

  const handleStartEdit = (business) => {
    setEditingId(business.id);
    setEditFields({
      bizName: business.bizName || '',
      bizType: business.bizType || '',
      bizStreetNo: business.bizStreetNo || business.bizStreetNumber || '',
      bizStreetName: business.bizStreetName || '',
      bizSuburb: business.bizSuburb || '',
      bizCountry: business.bizCountry || '',
      bizPhone: business.bizPhone || '',
      bizEmail: business.bizEmail || '',
      bizWeb: business.bizWeb || '',
    });
  };

  const handleSaveEdit = async (bizId) => {
    try {
      await db.collection('Business').doc(bizId).update(editFields);
      setBusinesses(prev => prev.map(b => b.id === bizId ? { ...b, ...editFields } : b));
      setEditingId(null);
    } catch (err) {
      console.error('Error saving edit:', err);
      alert('Failed to save changes.');
    }
  };

  const renderOwnerBadge = (business) => {
    const uid = business.uid || business.owner;
    if (!uid) return null;

    const owner = ownerMap[uid];
    const staff = staffMap[business.id];
    const ownerStaff = staff?.find(s => s.role === 'owner');

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Tooltip title={`UID: ${uid}`}>
          <Chip
            icon={<Person sx={{ fontSize: 14 }} />}
            label={owner ? `${owner.name}${owner.signupSource ? ` (${owner.signupSource})` : ''}` : uid.substring(0, 12) + '...'}
            size="small"
            variant="outlined"
            color="primary"
          />
        </Tooltip>
        {owner?.email && (
          <Typography variant="caption" color="text.secondary">{owner.email}</Typography>
        )}
        {staff && staff.length > 0 && (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {staff.map(s => (
              <Chip
                key={s.id}
                label={`${s.email || s.uid?.substring(0, 8) || '?'} (${s.role})`}
                size="small"
                variant="outlined"
                color={s.role === 'owner' ? 'success' : s.role === 'manager' ? 'warning' : 'default'}
                sx={{ fontSize: 11 }}
              />
            ))}
          </Box>
        )}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading new businesses...</Typography>
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (businesses.length === 0) {
    return (
      <Alert severity="info">
        No new businesses awaiting verification
      </Alert>
    );
  }

  // Compute source breakdown
  const sourceBreakdown = {};
  businesses.forEach(b => {
    const src = getSourceInfo(b).label;
    sourceBreakdown[src] = (sourceBreakdown[src] || 0) + 1;
  });

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Total New Businesses: {businesses.length}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
            {Object.entries(sourceBreakdown).map(([src, count]) => (
              <Chip key={src} label={`${src}: ${count}`} size="small" variant="outlined" />
            ))}
          </Box>
        </Box>
        <Button onClick={loadNewBusinesses} variant="outlined">
          Refresh
        </Button>
      </Box>
      <TabDescription>
        Venues added via the mobile app, claimed via the Piatto website, or bulk-imported but not yet verified.
        Each listing shows its source, the submitting user (owner), and any attached staff. Review details and
        images, send menu images to the CV module for extraction, edit fields, then approve or delete.
      </TabDescription>

      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mb: 2 }}>
        <Button variant="outlined" size="small" disabled={page === 0} onClick={() => { setPage(p => p - 1); setExpandedId(null); }}>
          Previous
        </Button>
        <Typography variant="body2">
          Page {page + 1} of {Math.ceil(businesses.length / PAGE_SIZE)} ({businesses.length} total)
        </Typography>
        <Button variant="outlined" size="small" disabled={(page + 1) * PAGE_SIZE >= businesses.length} onClick={() => { setPage(p => p + 1); setExpandedId(null); }}>
          Next
        </Button>
      </Box>

      <List>
        {businesses.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE).map((business) => {
          const sourceInfo = getSourceInfo(business);
          const isEditing = editingId === business.id;

          return (
            <Card key={business.id} sx={{ mb: 2 }}>
              <ListItem sx={{ py: 2 }}>
                <ListItemText
                  sx={{ cursor: 'pointer', flex: 1 }}
                  onClick={() => handleToggleExpand(business.id)}
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Business color="primary" />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {business.bizName || 'Unnamed Business'}
                      </Typography>
                      <Chip
                        icon={sourceInfo.icon}
                        label={sourceInfo.label}
                        size="small"
                        color={sourceInfo.color}
                        variant="outlined"
                      />
                      {business.bizStripeId && (
                        <Chip label="Stripe Connected" size="small" color="success" />
                      )}
                    </Box>
                  }
                  secondary={
                    <Box sx={{ mt: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        {business.bizAddress || [business.bizStreetNo || business.bizStreetNumber, business.bizStreetName, business.bizSuburb].filter(Boolean).join(' ') || 'No address provided'} {' '}
                        • Submitted {(business.timestamp || business.importedAt)?.toDate?.()?.toLocaleDateString?.() || 'Unknown'}
                      </Typography>
                      {renderOwnerBadge(business)}
                    </Box>
                  }
                />
                <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                  <Tooltip title="Preview media">
                    <IconButton size="small" onClick={() => setPreviewBusiness(business)}>
                      <Visibility />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Send to CV for menu extraction">
                    <IconButton size="small" color="secondary" onClick={() => setCvVenue(business)}>
                      <AutoAwesome />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit details">
                    <IconButton size="small" onClick={() => handleStartEdit(business)}>
                      <Edit />
                    </IconButton>
                  </Tooltip>
                  <Button
                    variant="outlined"
                    color="secondary"
                    size="small"
                    startIcon={sendingStripe === business.id ? <CircularProgress size={14} /> : <Payment />}
                    onClick={() => handleSendStripeOnboarding(business)}
                    disabled={sendingStripe === business.id}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    {sendingStripe === business.id ? 'Sending...' : 'Stripe'}
                  </Button>
                  <Tooltip title="Delete business">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteBusiness(business)}
                      disabled={deleting === business.id}
                    >
                      {deleting === business.id ? <CircularProgress size={16} /> : <Delete />}
                    </IconButton>
                  </Tooltip>
                  <Box sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }} onClick={() => handleToggleExpand(business.id)}>
                    {expandedId === business.id ? <ExpandLess /> : <ExpandMore />}
                  </Box>
                </Box>
              </ListItem>

              <Collapse in={expandedId === business.id} timeout="auto" unmountOnExit>
                <CardContent sx={{ bgcolor: 'grey.50', borderTop: 1, borderColor: 'divider' }}>
                  {/* Inline Edit Mode */}
                  {isEditing && (
                    <Box sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'primary.light', borderRadius: 1, bgcolor: 'primary.50' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>Edit Business Details</Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField fullWidth size="small" label="Business Name" value={editFields.bizName}
                            onChange={e => setEditFields(f => ({ ...f, bizName: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <TextField fullWidth size="small" label="Type" value={editFields.bizType}
                            onChange={e => setEditFields(f => ({ ...f, bizType: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <TextField fullWidth size="small" label="Street No" value={editFields.bizStreetNo}
                            onChange={e => setEditFields(f => ({ ...f, bizStreetNo: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField fullWidth size="small" label="Street Name" value={editFields.bizStreetName}
                            onChange={e => setEditFields(f => ({ ...f, bizStreetName: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField fullWidth size="small" label="Suburb" value={editFields.bizSuburb}
                            onChange={e => setEditFields(f => ({ ...f, bizSuburb: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField fullWidth size="small" label="Country" value={editFields.bizCountry}
                            onChange={e => setEditFields(f => ({ ...f, bizCountry: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField fullWidth size="small" label="Phone" value={editFields.bizPhone}
                            onChange={e => setEditFields(f => ({ ...f, bizPhone: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField fullWidth size="small" label="Email" value={editFields.bizEmail}
                            onChange={e => setEditFields(f => ({ ...f, bizEmail: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField fullWidth size="small" label="Website" value={editFields.bizWeb}
                            onChange={e => setEditFields(f => ({ ...f, bizWeb: e.target.value }))} />
                        </Grid>
                      </Grid>
                      <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                        <Button variant="contained" size="small" startIcon={<Save />} onClick={() => handleSaveEdit(business.id)}>
                          Save
                        </Button>
                        <Button variant="outlined" size="small" startIcon={<Cancel />} onClick={() => setEditingId(null)}>
                          Cancel
                        </Button>
                      </Box>
                    </Box>
                  )}

                  <Grid container spacing={3}>
                    {/* Basic Information */}
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        <Business sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                        Business Details
                      </Typography>
                      <Box sx={{ pl: 3 }}>
                        <Typography><strong>Type:</strong> {business.bizType || 'N/A'}</Typography>
                        <Typography><strong>Cuisine:</strong> {business.bizCuisines?.join(', ') || 'N/A'}</Typography>
                        <Typography><strong>Average Price:</strong> ${business.bizAveragePrice || 'N/A'}</Typography>
                        <Typography><strong>Source:</strong> {sourceInfo.label}</Typography>
                      </Box>
                    </Grid>

                    {/* Address */}
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        <LocationOn sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                        Address
                      </Typography>
                      <Box sx={{ pl: 3 }}>
                        <Typography>
                          {[
                            business.bizUnitNo,
                            business.bizStreetNo || business.bizStreetNumber,
                            business.bizStreetName,
                            business.bizStreetType,
                          ].filter(Boolean).join(' ')}
                        </Typography>
                        <Typography>
                          {business.bizSuburb} {business.bizState} {business.bizPostCode}
                        </Typography>
                        {business.bizCountry && (
                          <Typography>{business.bizCountry}</Typography>
                        )}
                      </Box>
                    </Grid>

                    {/* Contact */}
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Contact Information
                      </Typography>
                      <Box sx={{ pl: 3 }}>
                        {business.bizPhone && (
                          <Typography>
                            <Phone sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                            {business.bizPhone}
                          </Typography>
                        )}
                        {business.bizEmail && (
                          <Typography>
                            <Email sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                            {business.bizEmail}
                          </Typography>
                        )}
                        {business.bizWeb && (
                          <Typography>
                            <Language sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                            <a href={business.bizWeb} target="_blank" rel="noopener noreferrer">
                              {business.bizWeb}
                            </a>
                          </Typography>
                        )}
                      </Box>
                    </Grid>

                    {/* Trading Hours */}
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        <Schedule sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                        Trading Hours
                      </Typography>
                      <Box sx={{ pl: 3 }}>
                        {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => {
                          const open = business[`biz${day}Open`];
                          const close = business[`biz${day}Close`];
                          return (
                            <Typography key={day} variant="body2">
                              <strong>{day}:</strong> {open && close ? `${open} - ${close}` : 'Closed'}
                            </Typography>
                          );
                        })}
                      </Box>
                    </Grid>

                    {/* Seating */}
                    {business.bizTableTotal && (
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Seating
                        </Typography>
                        <Box sx={{ pl: 3 }}>
                          <Typography><strong>Total Tables:</strong> {business.bizTableTotal}</Typography>
                          <Typography><strong>Total Seats:</strong> {business.bizTotalSeatNo}</Typography>
                          {business.bizInsideLowTableNo && (
                            <Typography>Inside Low: {business.bizInsideLowTableNo} tables, {business.bizInsideLowSeatNo} seats</Typography>
                          )}
                          {business.bizOutsideLowTableNo && (
                            <Typography>Outside: {business.bizOutsideLowTableNo} tables, {business.bizOutsideLowSeatNo} seats</Typography>
                          )}
                        </Box>
                      </Grid>
                    )}

                    {/* Accessibility */}
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Accessibility Features
                      </Typography>
                      <Box sx={{ pl: 3 }}>
                        {business.bizDisBath && <Chip label="Accessible Bathroom" size="small" sx={{ mr: 1, mb: 1 }} />}
                        {business.disParking && <Chip label="Accessible Parking" size="small" sx={{ mr: 1, mb: 1 }} />}
                        {business.bizEntry === 'Ramp' && <Chip label="Ramp Entry" size="small" sx={{ mr: 1, mb: 1 }} />}
                        {business.bizAccessibilityDetails && (
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            <strong>Details:</strong> {business.bizAccessibilityDetails}
                          </Typography>
                        )}
                      </Box>
                    </Grid>

                    {/* Owner & Staff */}
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        <Person sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                        Owner & Staff
                      </Typography>
                      <Box sx={{ pl: 3 }}>
                        {renderOwnerBadge(business)}
                      </Box>
                    </Grid>

                    {/* Images — bizPic array (mobile submit) */}
                    {Array.isArray(business.bizPic) && business.bizPic.length > 0 && (
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Venue Photos ({business.bizPic.length})
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                          {business.bizPic.map((img, idx) => (
                            <img
                              key={idx}
                              src={img}
                              alt={`Venue ${idx + 1}`}
                              style={{ width: 150, height: 150, objectFit: 'cover', borderRadius: 8 }}
                            />
                          ))}
                        </Box>
                      </Grid>
                    )}

                    {/* Menu Photos — bizMenuPhotos array (mobile submit) */}
                    {Array.isArray(business.bizMenuPhotos) && business.bizMenuPhotos.length > 0 && (
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Menu Photos ({business.bizMenuPhotos.length})
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                          {business.bizMenuPhotos.map((img, idx) => (
                            <img
                              key={idx}
                              src={img}
                              alt={`Menu ${idx + 1}`}
                              style={{ width: 150, height: 150, objectFit: 'cover', borderRadius: 8 }}
                            />
                          ))}
                        </Box>
                      </Grid>
                    )}

                    {/* Legacy images field */}
                    {business.bizImages && business.bizImages.length > 0 && (
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Images
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                          {business.bizImages.map((img, idx) => (
                            <img
                              key={idx}
                              src={img}
                              alt={`Business ${idx + 1}`}
                              style={{ width: 150, height: 150, objectFit: 'cover', borderRadius: 8 }}
                            />
                          ))}
                        </Box>
                      </Grid>
                    )}

                    {/* Action Buttons */}
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2, flexWrap: 'wrap' }}>
                        <Button
                          variant="contained"
                          color="secondary"
                          startIcon={<AutoAwesome />}
                          onClick={() => setCvVenue(business)}
                        >
                          Send to CV
                        </Button>
                        <Button
                          variant="contained"
                          color="success"
                          size="large"
                          startIcon={verifying === business.id ? <CircularProgress size={20} /> : <CheckCircle />}
                          onClick={() => handleVerifyBusiness(business)}
                          disabled={verifying === business.id}
                          sx={{ px: 6, py: 1.5 }}
                        >
                          {verifying === business.id ? 'Verifying...' : 'Verify Business'}
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          startIcon={deleting === business.id ? <CircularProgress size={16} /> : <Delete />}
                          onClick={() => handleDeleteBusiness(business)}
                          disabled={deleting === business.id}
                        >
                          Delete
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Collapse>
            </Card>
          );
        })}
      </List>

      {previewBusiness && (
        <MediaPreviewDialog
          open={!!previewBusiness}
          onClose={() => setPreviewBusiness(null)}
          business={previewBusiness}
        />
      )}

      {cvVenue && (
        <AdminMenuCVImport
          venue={cvVenue}
          open={!!cvVenue}
          onClose={() => setCvVenue(null)}
          onImported={() => {
            setCvVenue(null);
            loadNewBusinesses();
          }}
        />
      )}
    </Box>
  );
};

export default NewBusinesses;
